import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertAgencySchema, type Agency } from "db/schema"; 
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../supabaseClient";

type AgencyFormProps = {
  onSuccess: (newAgency: Agency) => void; 
  initialData?: Agency | null;
};

export default function AgencyForm({
  onSuccess,
  initialData,
}: AgencyFormProps) {
  const { toast } = useToast();
  const form = useForm({
    resolver: zodResolver(insertAgencySchema),
    defaultValues: initialData || {
      name: "",
      phone: "",
      email: "",
      commissionPercentage: "",
    },
  });

  const onSubmit = async (values: any) => {
    try {
      const transformedValues = {
        ...values,
        commissionPercentage: Number(values.commissionPercentage), 
      };

      const tableName = "agency";
      const { data, error } = initialData
        ? await supabase
            .from(tableName)
            .update(transformedValues)
            .eq("id", initialData.id)
            .select()
        : await supabase
            .from(tableName)
            .insert([transformedValues])
            .select();

      if (error) {
        console.error("Supabase error:", error);
        throw new Error(error.message || "Failed to save agency");
      }

      toast({
        title: "Success",
        description: initialData
          ? "Agency updated successfully"
          : "Agency created successfully",
      });

     
      if (data && data[0]) {
        onSuccess(data[0]); 
      }

    } catch (error: any) {
      console.error("Agency form error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save agency",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input {...field} id="name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input type="tel" {...field} id="phone" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" {...field} id="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="commissionPercentage"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Commission Percentage</FormLabel>
              <FormControl>
                <Input type="number" {...field} id="commissionPercentage" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full">
          {initialData ? "Update Agency" : "Create Agency"}
        </Button>
      </form>
    </Form>
  );
}
